<template>
  <base-view id="support-portal-home">
    <div class="w-full page-fill bg-gray-lighter">
      <div
        class="relative w-full bg-center bg-cover"
        :style="{ backgroundImage: 'url(' + heroImage + ')' }"
      >
        <div class="absolute inset-0 hero-overlay"></div>
        <section
          class="relative flex flex-col items-center gap-4 max-w-3xl px-6 pt-16 pb-10 mx-auto text-center"
        >
          <h1 class="m-0 text-4xl font-extrabold leading-tight text-gray-900">
            How can we help?
          </h1>
          <p class="max-w-lg m-0 text-lg text-gray-700">
            Answers about your {{ audienceNoun }} — contributions, claims,
            cards, and more.
          </p>
          <div
            class="audience-toggle flex p-1 mt-1 rounded-full"
            role="group"
            aria-label="Choose your audience"
          >
            <button
              type="button"
              @click="selectAudience('participant')"
              :aria-pressed="audience === 'participant'"
              class="px-5 py-2 text-sm font-bold rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
              :class="
                audience === 'participant'
                  ? 'bg-white text-primary'
                  : 'bg-transparent text-gray-700'
              "
            >
              I'm a Participant
            </button>
            <button
              type="button"
              @click="selectAudience('employer')"
              :aria-pressed="audience === 'employer'"
              class="px-5 py-2 text-sm font-bold rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
              :class="
                audience === 'employer'
                  ? 'bg-white text-primary'
                  : 'bg-transparent text-gray-700'
              "
            >
              I'm an Employer
            </button>
          </div>
          <div class="relative w-full max-w-xl mt-2">
            <input
              type="text"
              ref="portalSearch"
              v-model="searchValue"
              placeholder='Search articles, e.g. "contribution limit"'
              aria-label="Search support articles"
              @keyup.enter="commitSearch"
              class="box-border w-full py-4 pl-12 pr-11 text-base bg-white border border-gray-dark rounded-lg shadow-sm outline-none focus:border-primary-light focus:ring-2 focus:ring-primary-light"
            />
            <span
              class="absolute flex items-center justify-center w-5 h-5 text-gray-500 pointer-events-none left-4 search-icon"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                />
              </svg>
            </span>
            <button
              v-show="searchValue"
              type="button"
              @click="searchValue = ''"
              aria-label="Clear search"
              class="absolute flex items-center justify-center w-5 h-5 text-gray-500 cursor-pointer right-4 search-icon hover:text-gray-700"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M6 18 18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </section>
      </div>

      <section
        v-if="zendeskError"
        class="w-full max-w-2xl px-6 pb-16 mx-auto text-center"
      >
        <p class="mb-4 text-gray-700">
          We couldn't load support articles. Please check your connection and
          try again.
        </p>
        <button
          type="button"
          @click="fetchZendeskData"
          class="px-5 py-2 text-sm font-bold text-white rounded-full bg-primary"
        >
          Try again
        </button>
      </section>

      <section v-else-if="hasQuery" class="w-full max-w-2xl px-6 mx-auto mb-12">
        <span
          aria-live="polite"
          class="text-xs font-bold tracking-wide text-gray-700 uppercase"
          >{{ searchResultsLabel }}</span
        >
        <div class="flex flex-col gap-2 mt-3">
          <ArticleListItem
            v-for="article in searchResults"
            :key="article.id"
            :title="article.title"
            :excerpt="sectionNameFor(article)"
            :to="`/support-portal/articles/${article.id}`"
          />
          <div v-if="!searchResults.length" class="py-6">
            <p class="mb-1 text-sm font-semibold text-gray-900">
              No results for "{{ debouncedSearch }}"
            </p>
            <p class="mb-4 text-sm text-gray-600">
              Try different keywords, or reach out and we'll help you directly.
            </p>
            <div class="flex flex-wrap gap-3">
              <a
                href="tel:8887221223"
                class="px-5 py-2 text-sm font-bold text-white rounded-full bg-primary hover:bg-primary-mid transition-colors"
                >Call 888.722.1223</a
              >
              <a
                href="mailto:info@rmrbenefits.com"
                class="px-5 py-2 text-sm font-bold text-gray-700 bg-white border border-gray-300 rounded-full hover:border-primary hover:text-primary transition-colors"
                >Send an email</a
              >
            </div>
          </div>
        </div>
      </section>

      <template v-else-if="loading">
        <section class="w-full max-w-4xl px-6 pb-16 mx-auto">
          <span class="text-xs font-bold tracking-wide text-gray-700 uppercase"
            >Browse by topic</span
          >
          <div class="topic-grid mt-3">
            <div
              v-for="n in 6"
              :key="n"
              class="flex flex-col overflow-hidden bg-white border border-gray-200 rounded-xl animate-pulse"
            >
              <div class="w-full h-28 bg-gray-200"></div>
              <div class="flex flex-col flex-1 gap-3 p-5">
                <div class="w-3/4 h-4 bg-gray-200 rounded"></div>
                <div class="w-full h-3 bg-gray-200 rounded"></div>
                <div class="w-1/3 h-3 mt-auto bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        </section>
      </template>

      <template v-else>
        <section
          v-if="popularArticles.length"
          class="w-full max-w-4xl px-6 mx-auto mb-12"
        >
          <span class="text-xs font-bold tracking-wide text-gray-700 uppercase"
            >Popular articles</span
          >
          <div class="flex flex-wrap gap-2 mt-3">
            <router-link
              v-for="article in popularArticles"
              :key="article.id"
              :to="`/support-portal/articles/${article.id}`"
              class="px-4 py-2 text-sm font-semibold bg-white border border-gray-200 rounded-full transition-colors hover:border-primary hover:text-primary"
            >
              {{ article.title }}
            </router-link>
          </div>
        </section>

        <section class="w-full max-w-4xl px-6 pb-16 mx-auto">
          <span class="text-xs font-bold tracking-wide text-gray-700 uppercase"
            >Browse by topic</span
          >
          <div class="topic-grid mt-3">
            <router-link
              v-for="topic in topics"
              :key="topic.id"
              :to="`/support-portal/topics/${topic.id}`"
              class="flex flex-col overflow-hidden bg-white border border-gray-200 rounded-xl transition hover:border-primary hover:shadow-md"
            >
              <img
                v-if="topic.image"
                :src="topic.image.src"
                :alt="topic.name"
                loading="lazy"
                class="w-full h-28"
                :style="{
                  objectFit: topic.image.size,
                  objectPosition: topic.image.position,
                  transform: topic.image.scale
                    ? `scale(${topic.image.scale})`
                    : undefined
                }"
              />
              <div
                v-else
                class="flex items-center justify-center w-full h-28 bg-primary-50"
              >
                <TopicIcon :icon="topic.icon" />
              </div>
              <div class="flex flex-col flex-1 gap-3 p-5">
                <div>
                  <div class="mb-1 text-base font-bold text-gray-900">
                    {{ topic.name }}
                  </div>
                  <div class="text-sm leading-snug text-gray-700">
                    {{ topic.blurb }}
                  </div>
                </div>
                <div class="mt-auto text-xs font-bold text-primary">
                  <template v-if="topic.subsectionCount">
                    {{ topic.subsectionCount }} topic<span
                      v-if="topic.subsectionCount !== 1"
                      >s</span
                    >
                    &middot;
                  </template>
                  {{ topic.count }} article<span v-if="topic.count !== 1"
                    >s</span
                  >
                </div>
                <div
                  v-if="topic.featuredArticle"
                  class="flex items-center gap-1 pt-2 border-t border-gray-100 text-xs text-gray-500 truncate"
                >
                  <span class="text-primary font-bold flex-shrink-0">★</span>
                  <span class="truncate">{{
                    topic.featuredArticle.title
                  }}</span>
                </div>
              </div>
            </router-link>
          </div>
          <p v-if="!topics.length" class="py-4 text-sm text-gray-700">
            No topics found for this audience yet.
          </p>
        </section>
      </template>
    </div>
  </base-view>
</template>

<script>
import Fuse from "fuse.js";
import { mapGetters, mapActions } from "vuex";
import BaseView from "@/components/layout/BaseView.vue";
import TopicIcon from "./components/TopicIcon.vue";
import ArticleListItem from "./components/ArticleListItem.vue";
import { excerpt } from "./utils";
import { imageForSection, iconForSection } from "./topicVisuals";

const AUDIENCE_CATEGORY_IDS = {
  participant: 360002944813,
  employer: 360002944833
};

export default {
  name: "SupportRedesignHome",
  components: { BaseView, TopicIcon, ArticleListItem },
  data() {
    return {
      audience: "participant",
      searchValue: "",
      debouncedSearch: "",
      debounceTimer: null,
      // Recompressed copy of bg-support.jpg (used at full quality on the
      // production /support page) — same dimensions, much smaller file size,
      // since the hero here doesn't need the original's higher bitrate.
      heroImage: require("@/assets/bg-support-optimized.jpg")
    };
  },
  watch: {
    searchValue(value) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.debouncedSearch = value;
      }, 180);
    },
    audience() {
      this.syncQuery();
    },
    "$route.query.q"(val) {
      const q = val || "";
      this.searchValue = q;
      this.debouncedSearch = q;
      if (q) {
        this.$nextTick(() => {
          if (this.$refs.portalSearch) this.$refs.portalSearch.focus();
        });
      }
    }
  },
  mounted() {
    if (this.$route.query.q) {
      this.$nextTick(() => {
        if (this.$refs.portalSearch) this.$refs.portalSearch.focus();
      });
    }
  },
  created() {
    const { q, audience } = this.$route.query;
    if (audience === "employer" || audience === "participant") {
      this.audience = audience;
    }
    if (q) {
      this.searchValue = q;
      this.debouncedSearch = q;
    }
  },
  beforeDestroy() {
    clearTimeout(this.debounceTimer);
  },
  computed: {
    ...mapGetters(["allZendeskData", "promotedArticles", "zendeskError"]),
    loading() {
      return !(this.allZendeskData && this.allZendeskData.sections);
    },
    audienceNoun() {
      return this.audience === "participant" ? "HSA" : "benefit plans";
    },
    audienceCategoryId() {
      return AUDIENCE_CATEGORY_IDS[this.audience];
    },
    sections() {
      return (
        this.$store.getters.sectionsByCategoryId(this.audienceCategoryId) || []
      );
    },
    topics() {
      return this.sections.map(section => ({
        id: section.id,
        name: section.name,
        blurb:
          section.description && section.description.trim()
            ? section.description
            : `Articles about ${section.name}.`,
        icon: iconForSection(section.id),
        image: imageForSection(section.id),
        count: (section.articles || []).length,
        subsectionCount: section.subsections ? section.subsections.length : 0,
        featuredArticle: (section.articles || []).find(a => a.promoted) || null
      }));
    },
    popularArticles() {
      return (this.promotedArticles || []).slice(0, 5);
    },
    hasQuery() {
      return this.debouncedSearch.trim().length > 0;
    },
    searchResults() {
      // Search spans every article regardless of audience — the audience
      // toggle only scopes which topic cards are shown, not search.
      if (!this.hasQuery) return [];
      const articles =
        (this.allZendeskData && this.allZendeskData.articles) || [];
      const fuse = new Fuse(articles, {
        keys: [
          { name: "title", weight: 0.7 },
          { name: "body", weight: 0.3 }
        ],
        threshold: 0.35
      });
      return fuse.search(this.debouncedSearch).map(result => result.item);
    },
    searchResultsLabel() {
      const count = this.searchResults.length;
      return `${count} result${count === 1 ? "" : "s"} for "${
        this.debouncedSearch
      }"`;
    }
  },
  methods: {
    ...mapActions(["fetchZendeskData"]),
    selectAudience(audience) {
      this.audience = audience;
    },
    commitSearch() {
      clearTimeout(this.debounceTimer);
      this.debouncedSearch = this.searchValue;
      this.syncQuery();
      this.$el.querySelector("input[type=text]").blur();
    },
    syncQuery() {
      const query = {};
      if (this.debouncedSearch) query.q = this.debouncedSearch;
      if (this.audience !== "participant") query.audience = this.audience;
      const current = this.$route.query;
      if (current.q === query.q && current.audience === query.audience) {
        return;
      }
      this.$router.replace({ path: "/support-portal", query }).catch(() => {});
    },
    excerptFor(article) {
      return excerpt(article.body);
    },
    sectionNameFor(article) {
      if (!this.allZendeskData || !this.allZendeskData.sections) return "";
      const section = this.allZendeskData.sections.find(
        s => s.id === article.section_id
      );
      return section ? section.name : "";
    }
  }
};
</script>

<style scoped>
/* Matches base-view's own min-height so the background fills the page even
   when there's little content (eg. a "0 results" search state), instead of
   the gray-lighter wrapper stopping short and showing plain white below. */
.page-fill {
  min-height: calc(100vh - 10rem);
}

/* text-base with no descenders (eg. "tttt") sits visually higher than the
   line box's true center, so a mathematically-centered icon (top: 50%)
   looks low by comparison. Nudge up slightly to match the optical center. */
.search-icon {
  top: calc(50% - 2px);
  transform: translateY(-50%);
}

.hero-overlay {
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.6) 0%,
    rgba(255, 255, 255, 0.88) 45%,
    #fafafa 75%,
    #fafafa 100%
  );
}

.audience-toggle {
  background: #e9e9e9;
}

.topic-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
}
</style>
